import React, { useState, useEffect, useRef, useReducer } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import "../../landing.css";
import InsightCard from "../../components/InsightCard";
import ErrorBoundary from "../../components/ErrorBoundary";
import { extractHashtags } from "../../utils/extractHashtags";

type FormatKey = "linkedin" | "tweet" | "thread" | "newsletter" | "script";
type AngleKey = "thoughtful" | "contrarian" | "bold" | "personal" | "viral";
type Mode = "distill" | "explore";

interface DistillResult {
  distilledInsight: string;
  rawThought?: string;
  topic: string;
  toneTags: string[];
  hook: string;
  pov: { agree: string; disagree: string };
}

interface ExploreMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

interface TopInsight {
  distilledInsight: string;
  topic: string;
  toneTags: string[];
  hook: string;
  score: number;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8085";

// Admin allow-list (mirrors Node/src/config/config.ts admin_emails). Keep in sync.
const ADMIN_EMAILS = ["admin@dkafka.com", "vikas@dkafka.com", "aditi@dkafka.com"];

const FORMATS: { label: string; key: FormatKey }[] = [
  { label: "LinkedIn", key: "linkedin" },
  { label: "X Post", key: "tweet" },
  { label: "X Thread", key: "thread" },
  { label: "Email", key: "newsletter" },
  { label: "Video Script", key: "script" },
];

// Carousel history — one entry per template generated for the current insight.
// Switching between history entries is instant (base64 is already in memory).
// History is cleared only when the user submits a new thought (new distillResult).
interface CarouselHistoryEntry {
  templateId:   string;
  colorTheme:   string;
  allThemePdfs: Record<string, string>; // base64 per theme key
  slides:       any;
  usageId:      string | null;
  renderer:     'html' | 'pptx';
  pptxBase64:   string | null;          // only for renderer=pptx
}

// ── Insight history reducer (CF-11) ─────────────────────────────────────────
// Full restore data for one history item. The GET /api/insights list now returns
// all of these so the frontend can restore without any API generate calls.
interface InsightHistoryItem {
  id:               number;
  distilledInsight: string;
  topic:            string;
  toneTags:         string[];
  hook:             string;
  pov:              { agree: string; disagree: string };
  // Outputs keyed by angle then format — null until the insight was actually generated
  outputs:          Record<string, Record<string, string>> | null;
  angle:            string | null;
  // Carousel restore — null when carousel was never generated for this insight
  carouselTemplateId: string | null;
  carouselColorTheme: string | null;
  carouselSlides:     any | null;
  createdAt:          string;
}

interface HistoryState {
  items:     InsightHistoryItem[];
  activeIdx: number | null; // null = current in-progress work; number = browsing history
}

type HistoryAction =
  | { type: 'SET_HISTORY';    items: InsightHistoryItem[] }
  | { type: 'PREPEND';        item:  InsightHistoryItem }
  | { type: 'PATCH_CAROUSEL'; id: number; templateId: string; colorTheme: string; slides: any }
  | { type: 'SET_ACTIVE';     idx: number }
  | { type: 'CLEAR_ACTIVE' }
  | { type: 'NAVIGATE';       dir: 1 | -1 };

function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
  switch (action.type) {
    case 'SET_HISTORY':
      return { ...state, items: action.items, activeIdx: null };

    case 'PREPEND':
      // Add new insight at front; deduplicate by id if somehow already present
      return {
        ...state,
        items: [action.item, ...state.items.filter(x => x.id !== action.item.id)],
      };

    case 'PATCH_CAROUSEL': {
      const items = state.items.map(x =>
        x.id === action.id
          ? { ...x, carouselTemplateId: action.templateId, carouselColorTheme: action.colorTheme, carouselSlides: action.slides }
          : x
      );
      return { ...state, items };
    }

    case 'SET_ACTIVE':
      return { ...state, activeIdx: action.idx };

    case 'CLEAR_ACTIVE':
      return { ...state, activeIdx: null };

    case 'NAVIGATE': {
      if (state.items.length === 0) return state;
      const current = state.activeIdx ?? 0;
      const next = Math.max(0, Math.min(current + action.dir, state.items.length - 1));
      return { ...state, activeIdx: next };
    }

    default:
      return state;
  }
}

// Carousel theme metadata — fetched from GET /api/carousel/templates.
// Only approved themes for the active template are shown.
interface CarouselThemeMeta {
  key: string;
  label: string;
  accent: string;
}
interface CarouselTemplateMeta {
  id: string;
  name: string;
  slideCount: number;
  hasThemes: boolean;
  approvedThemes: CarouselThemeMeta[];
}

const ANGLES: { label: string; key: AngleKey }[] = [
  { label: "Thoughtful", key: "thoughtful" },
  { label: "Contrarian", key: "contrarian" },
  { label: "Bold", key: "bold" },
  { label: "Personal", key: "personal" },
  { label: "Viral", key: "viral" },
];

const CHIPS = [
  { label: "Busy ≠ progress", text: "most founders confuse being busy with making progress" },
  { label: "Network > skills", text: "your network matters more than your skills at a certain level" },
  { label: "Build in public", text: "building in public is the best cold email you never send" },
  { label: "Clear thinking", text: "good writing is just clear thinking most people skip the thinking part" },
];

const EXPLORE_TOPICS = ["Leadership", "Productivity", "Career", "Strategy", "Founder Life", "Marketing"];

// ── Carousel summoning messages ───────────────────────────────────────────────
const CRAFTING_MSGS = [
  'Selecting the perfect template…',
  'Layering your ideas into slides…',
  'Applying the perfect theme…',
  'Adding the finishing touches…',
  'Almost ready…',
];
const REFINING_MSGS = [
  'Absorbing your feedback…',
  'Reshaping the slides…',
  'Almost there…',
];

const DistillPage: React.FC = () => {
  const location = useLocation();

  // ── Shared ──────────────────────────────────────────────────────────
  const [thought, setThought] = useState("");
  const [mode, setMode] = useState<Mode>("distill");

  // ── Distill output ───────────────────────────────────────────────────
  const [activeFormat, setActiveFormat] = useState<FormatKey>("linkedin");
  const [activeAngle, setActiveAngle] = useState<AngleKey>("thoughtful");
  const [distillResult, setDistillResult] = useState<DistillResult | null>(null);
  const [generatedOutputs, setGeneratedOutputs] = useState<Partial<Record<AngleKey, Record<FormatKey, string>>>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const [outputText, setOutputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [charCount, setCharCount] = useState("");
  const [progress, setProgress] = useState(0);
  const [showDemoCta, setShowDemoCta] = useState(false);
  const [copied, setCopied] = useState(false);
  const [demoError, setDemoError] = useState("");
  const [activeChip, setActiveChip] = useState(-1);

  // ── Explore ──────────────────────────────────────────────────────────
  const [exploreMessages, setExploreMessages] = useState<ExploreMessage[]>([]);
  const [exploreSessionId, setExploreSessionId] = useState<string | null>(null);
  const [exploreTopInsights, setExploreTopInsights] = useState<TopInsight[]>([]);
  const [isExploreLoading, setIsExploreLoading] = useState(false);
  const [isSummarising, setIsSummarising] = useState(false);
  const [summarisingIdx, setSummarisingIdx] = useState<number | null>(null);
  const [exploreChatInput, setExploreChatInput] = useState("");
  const [hasExploreStarted, setHasExploreStarted] = useState(false);

  // ── Auth ─────────────────────────────────────────────────────────────
  const [installId, setInstallId] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUserName, setAuthUserName] = useState("");
  const [loggedOutToast, setLoggedOutToast] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  // ── Carousel ─────────────────────────────────────────────────────────
  const [allCarouselTemplates, setAllCarouselTemplates] = useState<CarouselTemplateMeta[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);  // null = AI auto-selects
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [carouselSlides, setCarouselSlides] = useState<any>(null);
  const [carouselUsageId, setCarouselUsageId] = useState<string | null>(null);
  // Theme key is the FULL DB key (e.g. "colorshift-default", "pink-default") —
  // matches what GET /api/carousel/templates returns and what allThemePdfs is keyed by.
  // Empty initial value is fine: backend resolves the chosen template's first
  // approved theme when colorTheme is missing/unknown.
  const [carouselColorTheme, setCarouselColorTheme] = useState("");
  const [isCarouselLoading, setIsCarouselLoading] = useState(false);
  const [craftingMessage, setCraftingMessage]     = useState(CRAFTING_MSGS[0]);
  const [carouselError, setCarouselError] = useState(false);
  const [isCarouselRefining, setIsCarouselRefining] = useState(false);
  const [carouselRefineInput, setCarouselRefineInput] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCarouselDownloading, setIsCarouselDownloading] = useState(false);
  const [showCarousel, setShowCarousel] = useState(true);
  const [lastCarouselAngle, setLastCarouselAngle] = useState<AngleKey | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  // Empty initial value — backend selects the right template (and overrides
  // this state in the response). Hard-coding a stale default here used to
  // cause the viewer to query for the wrong template after a refresh.
  const [carouselTemplateId, setCarouselTemplateId] = useState("");
  // allThemePdfs: base64 per theme key — primary arrives fast, alt themes merged in background
  const [allThemePdfs, setAllThemePdfs] = useState<Record<string, string>>({});
  // allPdfDocs: PDF.js document per theme key — populated progressively as each PDF loads
  const [allPdfDocs, setAllPdfDocs] = useState<Record<string, any>>({});
  // altThemesLoading: true while background render of non-primary themes is in flight
  const [altThemesLoading, setAltThemesLoading] = useState(false);
  // renderer + pptxBase64 for the currently active carousel
  const [carouselRenderer, setCarouselRenderer] = useState<'html' | 'pptx'>('html');
  const [carouselPptxBase64, setCarouselPptxBase64] = useState<string | null>(null);
  // History: one entry per template generated for this insight (never deleted mid-session)
  const [carouselHistory, setCarouselHistory] = useState<CarouselHistoryEntry[]>([]);
  const [activeHistoryTemplateId, setActiveHistoryTemplateId] = useState<string | null>(null);
  const [downloadsThisMonth, setDownloadsThisMonth] = useState(0);
  const FREE_DOWNLOAD_LIMIT = 3;
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);

  // ── Persistence ──────────────────────────────────────────────────────
  // useReducer drives all insight history navigation (CF-11).
  // Every history transition (load, restore, navigate) goes through historyReducer.
  const [historyState, historyDispatch] = useReducer(historyReducer, { items: [], activeIdx: null });
  const insightHistory = historyState.items;          // shorthand for render code
  const activeHistoryIdx = historyState.activeIdx;

  // DB id of the insight that was just persisted (so we can PATCH carousel later)
  const [currentInsightDbId, setCurrentInsightDbId] = useState<number | null>(null);

  const [ihsCanLeft,  setIhsCanLeft]  = useState(false);
  const [ihsCanRight, setIhsCanRight] = useState(false);
  const ihsScrollRef = useRef<HTMLDivElement>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // ── Live activity graph ───────────────────────────────────────────────
  interface ActivityData {
    launchDate: string;
    totalUsers: number;        // anonymous sessions (activity graph)
    totalCarousels: number;
    registeredUsers: number;   // actual Google OAuth signups — nth-adopter counter
    earlyBirdUsed: number;     // = registeredUsers (real slots consumed)
    earlyBirdLimit: number;
    hourly: Array<{ hour: string; count: number }>;
  }
  const [activityData, setActivityData] = useState<ActivityData | null>(null);

  // ── Upgrade modal — payment transaction ID flow ───────────────────────
  const [paymentTxnId, setPaymentTxnId] = useState('');
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);

  type AuthModalReason = 'nav' | 'download' | 'refine' | 'copy' | 'share' | 'save' | 'publish';
  const [authModalReason, setAuthModalReason] = useState<AuthModalReason>('nav');

  const AUTH_MODAL_COPY: Record<AuthModalReason, { title: string; sub: string }> = {
    nav:      { title: 'Sign in to Distill',      sub: 'Save your insights, sync across devices, and unlock all features.' },
    download: { title: '1 month of carousels free', sub: `Sign up now — you'd be the #${(activityData?.registeredUsers ?? 0) + 1} early adopter. Unlimited carousels, first month free. No card needed.` },
    refine:   { title: 'Sign in to refine',       sub: 'Create a free account to keep refining your carousel.' },
    copy:     { title: 'Sign in to copy',         sub: 'Create a free account to copy and use your content.' },
    share:    { title: 'Sign in to share',        sub: 'Create a free account to share your insight and track reactions.' },
    save:     { title: 'Sign in to save',         sub: 'Create a free account to save and download your insight card.' },
    publish:  { title: 'Sign in to publish',      sub: 'Create a free account to publish this insight from Explore.' },
  };

  const openAuthModal = (reason: AuthModalReason = 'nav') => {
    setAuthModalReason(reason);
    setShowAuthModal(true);
  };

  // ── Share ────────────────────────────────────────────────────────────
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // ── Revise ───────────────────────────────────────────────────────────
  const [isRevising, setIsRevising] = useState(false);
  const [reviseInput, setReviseInput] = useState("");

  // ── Inline edit ──────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);

  // ── Notifications ────────────────────────────────────────────────────
  interface AppNotification {
    notification_id: number;
    type: string;
    title: string;
    body: string;
    is_read: boolean;
    created_at: string;
  }
  const [notifCount, setNotifCount] = useState(0);
  const [notifs, setNotifs] = useState<AppNotification[]>([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifPanelRef = useRef<HTMLDivElement | null>(null);

  // ── Refs ─────────────────────────────────────────────────────────────
  const typingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ctaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exploreChatBottomRef = useRef<HTMLDivElement | null>(null);
  const exploreMsgsRef = useRef<HTMLDivElement | null>(null);
  const outputSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (typingRef.current) clearInterval(typingRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
      if (ctaTimerRef.current) clearTimeout(ctaTimerRef.current);
    };
  }, []);

  // ── Crafting message cycle ────────────────────────────────────────────────
  // Reset to first message when a new load/refine starts, then cycle every 2.4s
  useEffect(() => {
    if (isCarouselLoading)   setCraftingMessage(CRAFTING_MSGS[0]);
    if (isCarouselRefining)  setCraftingMessage(REFINING_MSGS[0]);
  }, [isCarouselLoading, isCarouselRefining]);

  useEffect(() => {
    if (!isCarouselLoading && !isCarouselRefining) return;
    const msgs = isCarouselRefining ? REFINING_MSGS : CRAFTING_MSGS;
    let idx = 0;
    const id = setInterval(() => {
      idx = (idx + 1) % msgs.length;
      setCraftingMessage(msgs[idx]);
    }, 2400);
    return () => clearInterval(id);
  }, [isCarouselLoading, isCarouselRefining]);

  useEffect(() => {
    // ── install_id ───────────────────────────────────────────────────────
    let id = localStorage.getItem('distill_install_id');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('distill_install_id', id);
    }
    setInstallId(id);

    // ── auth check (no early return — session restore must always run) ───
    const stored = localStorage.getItem('distill_auth_token');
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split('.')[1]));
        if (payload.exp > Date.now() / 1000) {
          setIsAuthenticated(true);
          if (payload.name || payload.email) setAuthUserName(payload.name || payload.email);
          if (payload.role === 'admin' || ADMIN_EMAILS.includes((payload.email || '').toLowerCase())) setIsAdminUser(true);
          // Fetch isPro and linkedinHandle from profile endpoint (JWT-authed)
          axios.get(`${API_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${stored}` },
            withCredentials: true,
          }).then(r => {
            if (r.data?.isPro) setIsPro(true);
            if (r.data?.role === 'admin' || ADMIN_EMAILS.includes((r.data?.email || '').toLowerCase())) setIsAdminUser(true);
          }).catch(() => { });
        } else {
          localStorage.removeItem('distill_auth_token');
        }
      } catch {
        localStorage.removeItem('distill_auth_token');
      }
    } else {
      axios.get(`${API_URL}/api/user/me`, { withCredentials: true })
        .then(r => {
          if (r.data?.authenticated) {
            setIsAuthenticated(true);
            if (r.data.user?.name || r.data.user?.email) setAuthUserName(r.data.user.name || r.data.user.email);
            if (ADMIN_EMAILS.includes((r.data.user?.email || '').toLowerCase())) setIsAdminUser(true);
          }
        })
        .catch(() => { });

      // No real JWT — ensure a guest token exists so carousel generation works
      // without requiring Google sign-in. Guest JWT is stored separately from
      // the real auth token so the auth gate on download/share is unaffected.
      const existingGuest = localStorage.getItem('distill_guest_token');
      const guestValid = existingGuest ? (() => {
        try {
          const p = JSON.parse(atob(existingGuest.split('.')[1]));
          return p.exp > Date.now() / 1000;
        } catch { return false; }
      })() : false;

      if (!guestValid) {
        axios.post(`${API_URL}/auth/guest-login`, { install_id: id })
          .then(r => { if (r.data?.jwt) localStorage.setItem('distill_guest_token', r.data.jwt); })
          .catch(() => { /* non-critical — carousel will show auth prompt if this fails */ });
      }
    }

    // ── restore explore session ───────────────────────────────────────────
    axios.get(`${API_URL}/api/session?install_id=${encodeURIComponent(id)}`, { withCredentials: true })
      .then(r => {
        if (r.data && Array.isArray(r.data.messages) && r.data.messages.length > 0) {
          setExploreMessages(r.data.messages);
          setExploreTopInsights(r.data.insights ?? []);
          setExploreSessionId(r.data.exploreSessionId ?? null);
          setHasExploreStarted(true);
        }
      })
      .catch(() => { });

    // ── load insight history ──────────────────────────────────────────────
    axios.get(`${API_URL}/api/insights?install_id=${encodeURIComponent(id)}&pageSize=10`, { withCredentials: true })
      .then(r => {
        if (Array.isArray(r.data)) {
          historyDispatch({ type: 'SET_HISTORY', items: r.data as InsightHistoryItem[] });
        }
      })
      .catch(() => { });

    // ── load unread notification count ───────────────────────────────────
    axios.get(`${API_URL}/api/notifications/count?install_id=${encodeURIComponent(id)}`, { withCredentials: true })
      .then(r => { if (typeof r.data?.unread === 'number') setNotifCount(r.data.unread); })
      .catch(() => { });

    // ── fetch live activity data for launch graph ─────────────────────────
    axios.get(`${API_URL}/api/stats/activity`)
      .then(r => setActivityData(r.data))
      .catch(() => { });

    // ── fetch all carousel template metadata (approved themes come from backend) ─
    axios.get(`${API_URL}/api/carousel/templates`)
      .then(r => {
        const templates = (r.data?.templates as CarouselTemplateMeta[]) ?? [];
        setAllCarouselTemplates(templates);
      })
      .catch(() => { /* non-critical — carousel still works with defaults */ });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = exploreMsgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [exploreMessages, isExploreLoading]);

  // Recalculate insight scroll arrows whenever the list changes
  useEffect(() => {
    // Defer one frame so the DOM has painted the cards
    const id = setTimeout(updateIhsScroll, 60);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightHistory]);

  // Close notification panel on outside click
  useEffect(() => {
    if (!showNotifPanel) return;
    const handler = (e: MouseEvent) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target as Node)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifPanel]);

  // ── ?q param auto-submit (from Marketing page "Try It Out") ─────────────
  useEffect(() => {
    if (!installId) return;
    const q = new URLSearchParams(location.search).get('q');
    if (q) {
      const text = q.trim();
      setThought(text);
      runDemo(undefined, text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installId]);

  // ── Load PDFs progressively as each theme's base64 arrives ──────────────
  // Each theme loads independently — as soon as one is ready its button appears
  // and its canvas renders. No stale keys linger from old generations because
  // generateCarousel calls setAllThemePdfs with exactly the new set.
  useEffect(() => {
    const entries = Object.entries(allThemePdfs);
    if (entries.length === 0) return;
    let cancelled = false;
    (async () => {
      const pdfjsLib = await import('pdfjs-dist');
      (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`;
      // Load each theme in parallel; update allPdfDocs as each one completes
      entries.forEach(async ([themeKey, b64]) => {
        try {
          const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
          const doc   = await (pdfjsLib as any).getDocument({ data: bytes }).promise;
          if (!cancelled) {
            setAllPdfDocs(prev => ({ ...prev, [themeKey]: doc }));
          }
        } catch (e) {
          console.error('[PDF.js] failed to load theme', themeKey, e);
        }
      });
    })();
    return () => { cancelled = true; };
  }, [allThemePdfs]);

  // ── Render current page of the active theme's PDF to canvas ──────────────
  // activePdfDoc is derived below (allPdfDocs[carouselColorTheme])
  const activePdfDoc = allPdfDocs[carouselColorTheme] ?? null;
  useEffect(() => {
    if (!activePdfDoc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
        const page   = await activePdfDoc.getPage(currentSlideIndex + 1); // 1-indexed
        if (cancelled) return;
        const canvas = canvasRef.current!;
        const contW  = canvas.parentElement?.clientWidth ?? 400;
        const dpr    = window.devicePixelRatio || 1;
        const baseVp = page.getViewport({ scale: 1 });
        const scale  = (contW / baseVp.width) * dpr;
        const vp     = page.getViewport({ scale });
        canvas.width  = vp.width;   // physical pixels — CSS width:'100%' scales back to display size
        canvas.height = vp.height;
        const ctx    = canvas.getContext('2d')!;
        const task   = page.render({ canvasContext: ctx, viewport: vp });
        renderTaskRef.current = task;
        await task.promise;
      } catch (e: any) {
        if (e?.name !== 'RenderingCancelledException') console.error('[PDF.js] render error', e);
      }
    })();
    return () => { cancelled = true; };
  }, [activePdfDoc, currentSlideIndex]);

  // ── Helpers ──────────────────────────────────────────────────────────
  const typeOut = (text: string) => {
    if (typingRef.current) clearInterval(typingRef.current);
    setOutputText("");
    setIsTyping(true);
    setCharCount("");
    const chars = text.split("");
    let i = 0;
    const speed = Math.max(7, Math.min(16, 1100 / chars.length));
    typingRef.current = setInterval(() => {
      i++;
      setOutputText(chars.slice(0, i).join(""));
      if (i >= chars.length) {
        clearInterval(typingRef.current!);
        setIsTyping(false);
        setCharCount(`${chars.length} chars`);
      }
    }, speed);
  };

  const startFakeProgress = (from: number, to: number, interval: number) => {
    if (progressRef.current) clearInterval(progressRef.current);
    let current = from;
    const step = Math.ceil((to - from) / 20);
    progressRef.current = setInterval(() => {
      current = Math.min(current + step, to);
      setProgress(current);
      if (current >= to) clearInterval(progressRef.current!);
    }, interval);
  };

  const resetOutput = () => {
    setDistillResult(null);
    setGeneratedOutputs({});
    setShowOutput(false);
    setOutputText("");
    setCharCount("");
    setProgress(0);
    setShowDemoCta(false);
    setDemoError("");
    setActiveAngle("thoughtful");
    setActiveFormat("linkedin");
    // Wipe carousel state so the old carousel never persists into the next generation.
    // Set isCarouselLoading=true now so the summoning animation fires immediately
    // once distillResult arrives (rather than waiting for generateCarousel() to be called).
    setCarouselSlides(null);
    setCarouselError(false);
    setCarouselRenderer('html');
    setCarouselPptxBase64(null);
    setIsCarouselLoading(true);
    // New insight — wipe carousel history so we start fresh
    setCarouselHistory([]);
    setActiveHistoryTemplateId(null);
    setAllThemePdfs({});
    setAllPdfDocs({});
    setAltThemesLoading(false);
    // Return to current work view (not browsing history)
    historyDispatch({ type: 'CLEAR_ACTIVE' });
    setCurrentInsightDbId(null);
  };

  // ── Persistence helpers ───────────────────────────────────────────────

  // Prepend a fresh insight to the reducer state and refresh from server.
  const refreshInsightHistory = (id: string) => {
    axios.get(`${API_URL}/api/insights?install_id=${encodeURIComponent(id)}&pageSize=10`, { withCredentials: true })
      .then(r => {
        if (Array.isArray(r.data)) {
          historyDispatch({ type: 'SET_HISTORY', items: r.data as InsightHistoryItem[] });
        }
      })
      .catch(() => { });
  };

  // Persist insight to DB and capture the returned ID so we can later PATCH carousel data.
  // Returns the DB id so callers can pass it to patchInsightCarousel.
  const persistInsight = async (
    id: string,
    distill: { distilledInsight: string; rawThought?: string; topic: string; toneTags: string[]; hook: string; pov?: { agree?: string; disagree?: string } },
    outputs: Record<string, string>,
    angle: string,
  ): Promise<number | null> => {
    try {
      // Store as nested { [angle]: { linkedin, tweet, ... } } so multi-angle restores work.
      const nestedOutputs = { [angle]: outputs };
      const res = await axios.post(`${API_URL}/api/insights`, {
        install_id:       id,
        distilledInsight: distill.distilledInsight,
        rawThought:       distill.rawThought ?? null,
        topic:            distill.topic,
        toneTags:         distill.toneTags,
        hook:             distill.hook,
        pov:              distill.pov,
        outputs:          nestedOutputs,
        angle,
      }, { withCredentials: true });

      const dbId: number | null = res.data?.id ?? null;
      if (dbId !== null) setCurrentInsightDbId(dbId);

      // Prepend optimistically, then refresh from server for accurate ordering
      historyDispatch({
        type: 'PREPEND',
        item: {
          id:               dbId ?? Date.now(),
          distilledInsight: distill.distilledInsight,
          topic:            distill.topic,
          toneTags:         distill.toneTags,
          hook:             distill.hook,
          pov:              { agree: distill.pov?.agree ?? '', disagree: distill.pov?.disagree ?? '' },
          // Nested structure: { thoughtful: { linkedin, tweet, ... } }
          outputs:          { [angle]: outputs } as Record<string, Record<string, string>>,
          angle,
          carouselTemplateId: null,
          carouselColorTheme: null,
          carouselSlides:     null,
          createdAt:          new Date().toISOString(),
        },
      });
      refreshInsightHistory(id);
      return dbId;
    } catch {
      return null;
    }
  };

  // Fire-and-forget: patch carousel data on the persisted insight after generation.
  const patchInsightCarousel = (insightId: number, templateId: string, colorTheme: string, slides: any) => {
    axios.patch(`${API_URL}/api/insights/${insightId}/carousel`, {
      carouselTemplateId: templateId,
      carouselColorTheme: colorTheme,
      carouselSlides:     slides,
    }, { withCredentials: true })
      .then(() => {
        // Keep reducer in sync so restore works immediately if user revisits
        historyDispatch({ type: 'PATCH_CAROUSEL', id: insightId, templateId, colorTheme, slides });
      })
      .catch(() => { /* non-critical */ });
  };

  // Fire-and-forget: patch all-angle outputs on the persisted insight.
  // Called every time a new angle is generated so the DB always has the full map.
  // outputs shape: { thoughtful: { linkedin, tweet, thread, newsletter, script }, contrarian: { ... } }
  const patchInsightOutputs = (insightId: number, allOutputs: Partial<Record<AngleKey, Record<FormatKey, string>>>) => {
    // Flatten to a single-level map for storage: { "thoughtful.linkedin": "...", ... }?
    // No — store as nested JSON — the DB column is JSONB (or text-as-json), just send as-is.
    axios.patch(`${API_URL}/api/insights/${insightId}/outputs`, {
      outputs: allOutputs,
    }, { withCredentials: true }).catch(() => { /* non-critical */ });
  };

  // ── CF-11: Restore a historical insight (Option A — non-destructive) ────────
  // Loads the insight card and saved outputs without wiping current in-progress work.
  // If carousel_slides are persisted, re-renders them via POST /api/carousel/render
  // (Puppeteer only — no Gemini). Shows "Loading your carousel…" spinner.
  const restoreInsight = async (ins: InsightHistoryItem, idx: number) => {
    // Mark the active history item in the reducer (current work is preserved alongside)
    historyDispatch({ type: 'SET_ACTIVE', idx });

    // Restore the insight card
    setDistillResult({
      distilledInsight: ins.distilledInsight,
      topic:            ins.topic,
      toneTags:         ins.toneTags,
      hook:             ins.hook,
      pov:              { agree: ins.pov.agree, disagree: ins.pov.disagree },
    });

    // Restore saved outputs — the DB stores all angles as a nested map:
    // { thoughtful: { linkedin, tweet, thread, newsletter, script }, contrarian: {...} }
    const savedAngle = (ins.angle as AngleKey) ?? 'thoughtful';
    // ins.outputs may be the full nested map OR a flat map (old records without nesting).
    // Detect which shape it is: if the first value is a string, it's a flat map (legacy).
    let restoredOutputs: Partial<Record<AngleKey, Record<FormatKey, string>>> = {};

    if (ins.outputs) {
      const firstVal = Object.values(ins.outputs)[0];
      if (firstVal && typeof firstVal === 'object') {
        // Already nested: { thoughtful: { linkedin, ... }, contrarian: { ... } }
        restoredOutputs = ins.outputs as Partial<Record<AngleKey, Record<FormatKey, string>>>;
      } else if (firstVal && typeof firstVal === 'string') {
        // Legacy flat format: { linkedin: '...', tweet: '...' } — wrap in the saved angle
        restoredOutputs = { [savedAngle]: ins.outputs as unknown as Record<FormatKey, string> };
      }
    }

    const currentAngleOutputs = restoredOutputs[savedAngle] ?? null;

    setActiveAngle(savedAngle);
    setActiveFormat('linkedin');

    if (currentAngleOutputs) {
      setGeneratedOutputs(restoredOutputs);
      setShowOutput(true);
      typeOut(currentAngleOutputs.linkedin ?? '');
    } else {
      // Outputs were never generated (edge case) — show the card but nothing in the output panel
      setGeneratedOutputs({});
      setShowOutput(true);
      setOutputText('');
    }

    // Restore carousel from stored slides (no Gemini — Puppeteer only)
    if (ins.carouselSlides && ins.carouselTemplateId && ins.carouselColorTheme && isAuthenticated) {
      setIsCarouselLoading(true);
      setCarouselError(false);
      setCarouselSlides(ins.carouselSlides);
      setCarouselTemplateId(ins.carouselTemplateId);
      setCarouselColorTheme(ins.carouselColorTheme);
      setCurrentSlideIndex(0);
      setAllPdfDocs({});
      setAllThemePdfs({});
      try {
        const stored  = localStorage.getItem('distill_auth_token');
        const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
        const res = await axios.post(
          `${API_URL}/api/carousel/render`,
          {
            templateId: ins.carouselTemplateId,
            slides:     ins.carouselSlides,
            colorTheme: ins.carouselColorTheme,
          },
          { headers, withCredentials: true }
        );
        const allThemePdfs = res.data.allThemePdfs ?? {};
        setAllThemePdfs(allThemePdfs);
      } catch {
        // Non-critical — carousel restore failure doesn't break the text restore
        setCarouselError(true);
      } finally {
        setIsCarouselLoading(false);
      }
    } else {
      // No carousel data persisted — reset carousel to clean state
      setCarouselSlides(null);
      setAllThemePdfs({});
      setAllPdfDocs({});
    }

    // Set this as the current insight ID so any new carousel generate patches it
    setCurrentInsightDbId(ins.id);
    setTimeout(() => outputSectionRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // ── Distill path ─────────────────────────────────────────────────────
  const runDemo = async (chipIdx?: number, chipText?: string) => {
    const inputText = (chipText ?? thought).trim();
    if (!inputText || isRunning) return;
    if (typingRef.current) clearInterval(typingRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    if (ctaTimerRef.current) clearTimeout(ctaTimerRef.current);
    resetOutput();
    setIsRunning(true);
    setShowOutput(true);
    startFakeProgress(0, 45, 280);
    try {
      const distillRes = await axios.post(`${API_URL}/api/distill`, { rawThought: inputText }, { withCredentials: true });
      const distill: DistillResult = distillRes.data;
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(50);
      setDistillResult(distill);
      startFakeProgress(50, 90, 320);
      const genRes = await axios.post(
        `${API_URL}/api/generate`,
        {
          distilledInsight: distill.distilledInsight,
          topic: distill.topic,
          angle: "thoughtful",
          // Preserve any hashtags the user typed in their raw thought.
          userHashtags: extractHashtags(inputText),
        },
        { withCredentials: true }
      );
      const outputs: Record<FormatKey, string> = genRes.data.outputs;
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setGeneratedOutputs({ thoughtful: outputs });
      setIsRunning(false);
      typeOut(outputs.linkedin || "");
      ctaTimerRef.current = setTimeout(() => setShowDemoCta(true), 2200);
      // Persist insight and capture DB id — then generate carousel and PATCH with slide data
      const savedId = installId ? await persistInsight(installId, distill, outputs, "thoughtful") : null;
      // Auto-generate carousel in background — ready when user clicks the tab.
      // Pass the freshly-returned LinkedIn post explicitly: setGeneratedOutputs
      // hasn't flushed yet, so reading from state inside generateCarousel would
      // be empty for the auto-fire path.
      setLastCarouselAngle("thoughtful");
      generateCarousel(distill, carouselColorTheme, "thoughtful", undefined, savedId, null, outputs.linkedin);
    } catch (err: any) {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(0);
      setIsRunning(false);
      setShowOutput(false);
      setIsCarouselLoading(false);
      setDemoError("Something went wrong. Please try again.");
    }
  };

  const handleChip = (idx: number) => {
    if (mode !== "distill") return;
    const text = CHIPS[idx].text;
    setThought(text);
    setActiveChip(idx);
    runDemo(idx, text);
  };

  const handleFormat = (key: FormatKey) => {
    if (!distillResult) return;
    const outputs = generatedOutputs[activeAngle];
    if (!outputs) return;
    setActiveFormat(key);
    typeOut(outputs[key] || "");
  };

  const handleAngle = async (key: AngleKey) => {
    if (!distillResult || isRunning) return;
    setActiveAngle(key);
    if (generatedOutputs[key]) {
      typeOut(generatedOutputs[key]![activeFormat] || "");
      return;
    }
    setIsRunning(true);
    setOutputText("");
    setCharCount("");
    startFakeProgress(0, 85, 300);
    try {
      const genRes = await axios.post(
        `${API_URL}/api/generate`,
        {
          distilledInsight: distillResult.distilledInsight,
          topic: distillResult.topic,
          angle: key,
          // Preserve user-typed hashtags from the original raw thought across angle switches.
          userHashtags: extractHashtags(distillResult.rawThought),
        },
        { withCredentials: true }
      );
      const outputs: Record<FormatKey, string> = genRes.data.outputs;
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      const updatedOutputs = { ...generatedOutputs, [key]: outputs } as Partial<Record<AngleKey, Record<FormatKey, string>>>;
      setGeneratedOutputs(updatedOutputs);
      setIsRunning(false);
      typeOut(outputs[activeFormat] || "");
      // Persist updated all-angle outputs to DB (fire-and-forget)
      if (currentInsightDbId) patchInsightOutputs(currentInsightDbId, updatedOutputs);
      // Regenerate carousel only when angle changes (platform switches don't matter).
      // Pass the freshly-generated LinkedIn output for this angle so Gemini anchors
      // the carousel on the new angle's post (state may not be flushed yet).
      if (distillResult && key !== lastCarouselAngle) {
        setLastCarouselAngle(key);
        generateCarousel(distillResult, carouselColorTheme, key, undefined, undefined, null, outputs.linkedin);
      }
    } catch {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(0);
      setIsRunning(false);
    }
  };

  // ── Carousel derived values ───────────────────────────────────────────────
  // Slide count from the active PDF doc (source of truth — no hardcoding)
  const slideCount = activePdfDoc?.numPages ?? 0;
  // All themes for this template according to backend metadata
  const currentTemplateMeta = allCarouselTemplates.find(t => t.id === carouselTemplateId) ?? null;
  // Show theme button once PDF loaded OR while alt-themes are loading (show spinner placeholder)
  const approvedThemes = (currentTemplateMeta?.approvedThemes ?? []).filter(
    t => allPdfDocs[t.key] !== undefined
  );
  // Pending: approved themes not yet loaded, only shown during background alt-theme fetch
  const pendingThemes = altThemesLoading
    ? (currentTemplateMeta?.approvedThemes ?? []).filter(t => allPdfDocs[t.key] === undefined)
    : [];

  // ── Carousel helpers ──────────────────────────────────────────────────

  const generateCarousel = async (
    distill: DistillResult,
    colorTheme: string,
    angle: string,
    excludeTemplates?: string[],   // IDs already shown — Gemini won't re-pick them
    insightId?: number | null,     // DB id of the insight row — for PATCH carousel after generate
    templateIdOverride?: string | null, // explicit template pick — bypasses Gemini selector
    editedCaption?: string,        // LinkedIn post for this angle — anchors carousel copy on the post
  ) => {
    // Use real auth token if available, fall back to guest token.
    // Guest token is obtained automatically on mount (POST /auth/guest-login).
    // isAuthenticated is NOT required — guests can generate; only download/share is gated.
    const carouselToken = localStorage.getItem('distill_auth_token') || localStorage.getItem('distill_guest_token');
    if (!carouselToken) return;

    setIsCarouselLoading(true);
    setCarouselError(false);
    setCarouselSlides(null);
    setAltThemesLoading(false);
    setAllPdfDocs({});
    setAllThemePdfs({});
    try {
      const stored = carouselToken;
      const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
      // Resolve LinkedIn caption: explicit param > state for this angle > undefined.
      // Sent as `editedCaption` so Gemini anchors carousel copy on the actual post
      // the user is publishing — keeps carousel and post in voice/content alignment.
      const captionForCarousel =
        (editedCaption?.trim()) ||
        (generatedOutputs[angle as AngleKey]?.linkedin || '').trim() ||
        undefined;

      const res = await axios.post(
        `${API_URL}/api/carousel`,
        {
          distilledInsight:  distill.distilledInsight,
          hook:              distill.hook,
          topic:             distill.topic,
          angle,
          colorTheme,
          // Pass explicit template when user has picked one (overrides Gemini selector)
          ...(( templateIdOverride ?? selectedTemplateId) ? { template: templateIdOverride ?? selectedTemplateId } : {}),
          ...(excludeTemplates && excludeTemplates.length > 0 ? { excludeTemplates } : {}),
          ...(captionForCarousel ? { editedCaption: captionForCarousel } : {}),
        },
        { headers, withCredentials: true }
      );
      const slides           = res.data.slides;
      // Backend always returns templateId + colorTheme — these fallbacks are
      // last-resort guards only. Prefer carouselTemplateId state for fallback
      // (set by previous successful generate) over a hard-coded id that may
      // not exist in the DB.
      const templateId       = res.data.templateId  ?? carouselTemplateId ?? "";
      const resolvedTheme    = res.data.colorTheme  ?? colorTheme;
      const newAllThemePdfs  = res.data.allThemePdfs ?? { [resolvedTheme]: res.data.pdfBase64 };
      const renderer         = (res.data.renderer ?? 'html') as 'html' | 'pptx';
      const pptxB64          = res.data.pptxBase64 ?? null;

      setCarouselSlides(slides);
      setCarouselUsageId(res.data.usageId);
      setCarouselColorTheme(resolvedTheme);
      setCarouselTemplateId(templateId);
      setCarouselRenderer(renderer);
      setCarouselPptxBase64(pptxB64);
      if (typeof res.data.downloadsThisMonth === 'number') {
        setDownloadsThisMonth(res.data.downloadsThisMonth);
      }

      // Persist this generation in carousel history (replace if same template was seen before)
      const historyEntry: CarouselHistoryEntry = {
        templateId,
        colorTheme: resolvedTheme,
        allThemePdfs: newAllThemePdfs,
        slides,
        usageId: res.data.usageId ?? null,
        renderer,
        pptxBase64: pptxB64,
      };
      setCarouselHistory(prev => {
        const idx = prev.findIndex(e => e.templateId === templateId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = historyEntry;
          return next;
        }
        return [...prev, historyEntry];
      });
      setActiveHistoryTemplateId(templateId);

      // Reset to slide 1, then load primary theme PDF progressively
      setCurrentSlideIndex(0);
      setAllPdfDocs({});
      setAllThemePdfs(newAllThemePdfs);

      // ── Background: fetch alt theme PDFs (HTML templates only) ───────────
      // We already know which themes are approved for this template from the
      // initial GET /api/carousel/templates load. Fire a render-only request
      // for the remaining ones so the user doesn't wait for them upfront.
      if (res.data.renderer === 'html') {
        const templateMeta = allCarouselTemplates.find(t => t.id === templateId);
        const altThemeKeys = (templateMeta?.approvedThemes ?? [])
          .map(t => t.key)
          .filter(k => !newAllThemePdfs[k]);   // exclude themes we already have

        if (altThemeKeys.length > 0) {
          setAltThemesLoading(true);
          axios.post(
            `${API_URL}/api/carousel/render`,
            { templateId, slides, colorTheme: resolvedTheme, themeIds: altThemeKeys },
            { headers, withCredentials: true }
          ).then(renderRes => {
            const altPdfs: Record<string, string> = renderRes.data.allThemePdfs ?? {};
            // Merge alt PDFs — existing primary is preserved via spread order
            setAllThemePdfs(prev => ({ ...prev, ...altPdfs }));
            // Keep carousel history in sync
            setCarouselHistory(prev => prev.map(e =>
              e.templateId === templateId
                ? { ...e, allThemePdfs: { ...e.allThemePdfs, ...altPdfs } }
                : e
            ));
          }).catch(err => {
            console.warn('[carousel] alt theme background render failed:', (err as any)?.message);
          }).finally(() => {
            setAltThemesLoading(false);
          });
        }
      }

      // Persist carousel data to the insight row so future restores skip Gemini
      const targetId = insightId ?? currentInsightDbId;
      if (targetId) {
        patchInsightCarousel(targetId, templateId, resolvedTheme, slides);
      }
    } catch {
      setCarouselError(true);
    } finally {
      setIsCarouselLoading(false);
    }
  };


  const navigateCarousel = (idx: number) => {
    setCurrentSlideIndex(Math.max(0, Math.min(idx, slideCount - 1)));
  };

  const handleCarouselThemeChange = (theme: string) => {
    // All theme PDFs were pre-rendered by the backend — switch is instant, no API call.
    // The slide index is preserved so the user stays on the same slide.
    setCarouselColorTheme(theme);
  };

  // Switch to a previously generated template from history.
  // The base64 is already in memory — PDF.js re-parses it in <100ms.
  const switchCarouselHistory = (templateId: string) => {
    const entry = carouselHistory.find(e => e.templateId === templateId);
    if (!entry || templateId === activeHistoryTemplateId) return;
    setActiveHistoryTemplateId(templateId);
    setCarouselTemplateId(templateId);
    setCarouselColorTheme(entry.colorTheme);
    setCarouselSlides(entry.slides);
    setCarouselUsageId(entry.usageId);
    setCarouselRenderer(entry.renderer ?? 'html');
    setCarouselPptxBase64(entry.pptxBase64 ?? null);
    setCurrentSlideIndex(0);
    setAllPdfDocs({});               // brief blank while PDF.js re-parses (fast — base64 in RAM)
    setAllThemePdfs(entry.allThemePdfs); // triggers progressive load useEffect
  };

  const handleCarouselRefine = async () => {
    if (!carouselSlides || !carouselRefineInput.trim()) return;
    if (!isAuthenticated) { openAuthModal('refine'); return; }
    setIsCarouselRefining(true);
    try {
      const stored = localStorage.getItem('distill_auth_token');
      const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
      // Anchor refines on the active LinkedIn post, same as fresh generation.
      const captionForRefine =
        (generatedOutputs[activeAngle]?.linkedin || outputText || '').trim() || undefined;

      const res = await axios.post(
        `${API_URL}/api/carousel/refine`,
        {
          usageId:       carouselUsageId,
          currentSlides: carouselSlides,
          instruction:   carouselRefineInput,
          template:      carouselTemplateId,
          colorTheme:    carouselColorTheme,
          ...(captionForRefine ? { editedCaption: captionForRefine } : {}),
        },
        { headers, withCredentials: true }
      );
      setCarouselSlides(res.data.slides);
      setCarouselRefineInput("");
      // Replace all theme PDFs with refined versions; progressive load as each arrives
      setAllPdfDocs({});
      setAllThemePdfs(
        res.data.allThemePdfs ?? { [carouselColorTheme]: res.data.pdfBase64 }
      );
    } catch {
      // keep existing slides on refine failure
    } finally {
      setIsCarouselRefining(false);
    }
  };

  const handleCarouselDownload = () => {
    if (!isAuthenticated) { openAuthModal('download'); return; }
    // Free users get FREE_DOWNLOAD_LIMIT downloads/month; Pro and admin are unlimited
    if (!isPro && !isAdminUser && downloadsThisMonth >= FREE_DOWNLOAD_LIMIT) { setShowUpgradeModal(true); return; }
    const activePdfB64 = allThemePdfs[carouselColorTheme];
    if (!activePdfB64 || isCarouselDownloading) return;
    setIsCarouselDownloading(true);
    try {
      const bytes = atob(activePdfB64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "distill-carousel.pdf";
      a.click();
      URL.revokeObjectURL(url);
      // Mark as downloaded — response updates monthly quota
      if (carouselUsageId) {
        const stored = localStorage.getItem('distill_auth_token');
        const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
        axios.patch(
          `${API_URL}/api/carousel/download`,
          { usageId: carouselUsageId },
          { headers, withCredentials: true }
        ).then(r => {
          if (typeof r.data.downloadsThisMonth === 'number') {
            setDownloadsThisMonth(r.data.downloadsThisMonth);
          }
        }).catch(() => {});
      }
    } catch { /* ignore */ } finally {
      setIsCarouselDownloading(false);
    }
  };

  const handleDownloadPptx = () => {
    if (!isAuthenticated) { openAuthModal('download'); return; }
    if (!carouselPptxBase64) return;
    try {
      const bytes = atob(carouselPptxBase64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'distill-carousel.pptx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  // ── Carousel viewer — PDF canvas + control bar below ────────────────────
  // activePdfDoc is the loaded PDF.js document for the currently selected theme.
  // Theme buttons only appear once their PDF has been loaded (progressive reveal).
  // Renders the carousel preview panel. Three states:
  //   summoning  — shown while loading OR while allThemePdfs has data but PDF.js
  //                hasn't finished parsing yet (pdfPending). Prevents the panel
  //                collapsing to zero when isCarouselLoading and activePdfDoc lose sync.
  //   viewer     — mounts with reveal animation once activePdfDoc is set.
  //                Overlay spinner shown during refine (old slide stays visible).
  //   error      — only when activePdfDoc is null (no fallback to show).
  const renderCarouselPreview = () => {
    // Error state (only when no existing doc to fall back to)
    if (carouselError && !activePdfDoc) {
      return (
        <div className="carousel-preview-error">
          <span>Carousel generation failed.</span>
          {distillResult && (
            <button className="carousel-retry-btn" onClick={() => generateCarousel(distillResult!, carouselColorTheme, activeAngle)}>
              Retry →
            </button>
          )}
        </div>
      );
    }

    // No PDF yet — show summoning.
    // Also stays visible when allThemePdfs has data but PDF.js hasn't finished parsing yet
    // (isCarouselLoading becomes false before activePdfDoc is set due to React batching).
    if (!activePdfDoc) {
      const pdfPending = Object.keys(allThemePdfs).length > 0;
      if (isCarouselLoading || isCarouselRefining || pdfPending) {
        return (
          <div className="carousel-summoning">
            {/* ── Orbital stage ── */}
            <div className="carousel-summoning-stage">
              {/* Breathing concentric rings */}
              <div className="summon-ring summon-ring-1" />
              <div className="summon-ring summon-ring-2" />
              <div className="summon-ring summon-ring-3" />
              {/* Rotating arc spinner */}
              <div className="summon-spinner" />
              {/* Core glowing orb */}
              <div className="summon-orb">
                <span className="summon-orb-glyph">✦</span>
              </div>
              {/* Orbiting sparkle particles */}
              <span className="summon-spark summon-spark-1" aria-hidden>✦</span>
              <span className="summon-spark summon-spark-2" aria-hidden>◆</span>
              <span className="summon-spark summon-spark-3" aria-hidden>✦</span>
              <span className="summon-spark summon-spark-4" aria-hidden>·</span>
            </div>

            {/* Stage message — key forces re-mount → CSS fade-in each change */}
            <p key={craftingMessage} className="summon-message">
              {craftingMessage}
            </p>

            {/* Progress track */}
            <div className="summon-track">
              <div
                className="summon-fill"
                key={isCarouselRefining ? 'refine' : 'generate'}
              />
            </div>
          </div>
        );
      }
      return null;
    }

    // PDF loaded — show viewer. Overlay a spinner during refine.
    return (
      <div className="carousel-viewer">
        {/* ── Slide display — PDF rendered to canvas ── */}
        <div className="carousel-viewer-slide" style={{ position: 'relative' }}>
          <canvas
            ref={canvasRef}
            style={{ display: 'block', width: '100%', height: 'auto' }}
          />
          {/* Loading overlay — slides stay visible, no collapse */}
          {(isCarouselLoading || isCarouselRefining) && (
            <div className="carousel-viewer-overlay">
              <div className="carousel-loading-spinner" />
            </div>
          )}
        </div>

        {/* ── Control bar — only when there's navigation or theme choice ── */}
        {(slideCount > 1 || approvedThemes.length > 1) && (
        <div className="carousel-viewer-bar">
          {slideCount > 1 && (
            <button
              className="carousel-viewer-nav"
              onClick={() => navigateCarousel(currentSlideIndex - 1)}
              disabled={currentSlideIndex === 0}
              aria-label="Previous slide"
            >‹</button>
          )}
          {slideCount > 1 && (
            <span className="carousel-viewer-counter">
              {currentSlideIndex + 1} / {slideCount}
            </span>
          )}
          {slideCount > 1 && (
            <button
              className="carousel-viewer-nav"
              onClick={() => navigateCarousel(currentSlideIndex + 1)}
              disabled={currentSlideIndex >= slideCount - 1}
              aria-label="Next slide"
            >›</button>
          )}

          {/* Theme picker — ready buttons appear as PDFs load; pending buttons show while alt themes render in background */}
          {(approvedThemes.length > 1 || pendingThemes.length > 0) && (
            <div className="carousel-viewer-themes">
              {approvedThemes.map(t => (
                <button
                  key={t.key}
                  className={`carousel-viewer-theme-btn${carouselColorTheme === t.key ? ' active' : ''} carousel-theme-reveal`}
                  style={carouselColorTheme === t.key ? { borderColor: t.accent, color: t.accent } : {}}
                  onClick={() => handleCarouselThemeChange(t.key)}
                  title={t.label}
                >
                  {t.label}
                </button>
              ))}
              {pendingThemes.map(t => (
                <button
                  key={t.key}
                  className="carousel-viewer-theme-btn carousel-theme-pending"
                  disabled
                  title={`${t.label} — loading…`}
                >
                  {t.label} <span className="carousel-theme-spinner" />
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    );
  };

  const handleCopy = () => {
    console.log('[handleCopy] isAuthenticated:', isAuthenticated);
    if (!isAuthenticated) { openAuthModal('copy'); return; }
    navigator.clipboard.writeText(outputText).catch(() => { });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevise = async (instruction: string) => {
    if (!outputText || !distillResult || !instruction.trim()) return;
    setIsRevising(true);
    try {
      const res = await axios.post(`${API_URL}/api/revise`, {
        currentOutput: outputText,
        format: activeFormat,
        angle: activeAngle,
        distilledInsight: distillResult.distilledInsight,
        topic: distillResult.topic,
        instruction,
      }, { withCredentials: true });
      const revised = res.data?.output;
      if (revised) {
        setGeneratedOutputs(prev => ({
          ...prev,
          [activeAngle]: { ...(prev[activeAngle] ?? {}), [activeFormat]: revised },
        }));
        typeOut(revised);
        setReviseInput("");
      }
    } catch {
      // silent fail — user can retry
    } finally {
      setIsRevising(false);
    }
  };

  const handleEditStart = () => {
    if (isTyping || isRunning || isRevising) return;
    setIsEditing(true);
  };

  const handleEditDone = () => {
    setGeneratedOutputs(prev => ({
      ...prev,
      [activeAngle]: { ...(prev[activeAngle] ?? {}), [activeFormat]: outputText },
    }));
    setIsEditing(false);
  };

  const handleShare = async () => {
    if (!isAuthenticated) { openAuthModal('share'); return; }
    if (!distillResult || isSharing) return;
    setIsSharing(true);
    try {
      const outputs: Record<string, string> = {};
      Object.entries(generatedOutputs).forEach(([, fmts]) => {
        if (fmts) Object.assign(outputs, fmts);
      });
      const res = await axios.post(
        `${API_URL}/api/share`,
        {
          distilledInsight: distillResult.distilledInsight,
          topic: distillResult.topic,
          toneTags: distillResult.toneTags,
          hook: distillResult.hook,
          pov: distillResult.pov,
          outputs,
          angle: activeAngle,
        },
        { withCredentials: true }
      );
      setShareUrl(res.data.url);
      setShowShareModal(true);
    } catch (err) {
      console.error('[Share] failed:', err);
      setShareError(true);
      setTimeout(() => setShareError(false), 3000);
    }
    setIsSharing(false);
  };

  const handleCopyShareUrl = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).catch(() => { });
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const openShareWindow = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const handleDownloadCard = async () => {
    if (!isAuthenticated) { openAuthModal('save'); return; }
    if (!cardRef.current || !distillResult || isDownloading) return;
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true });
      canvas.toBlob((blob: Blob | null) => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'distill-insight.png';
        a.click();
        URL.revokeObjectURL(a.href);
      });
    } catch { }
    setIsDownloading(false);
  };

  // ── Explore path ─────────────────────────────────────────────────────
  const switchToExplore = () => {
    setMode("explore");
    setExploreChatInput("");
    resetOutput();
    setActiveChip(-1);
    // Preserve exploreMessages/exploreSessionId/exploreTopInsights across mode
    // switches. hasExploreStarted follows whether messages actually exist.
    if (exploreMessages.length > 0) setHasExploreStarted(true);
  };

  const startFreshExplore = () => {
    setExploreMessages([]);
    setExploreSessionId(null);
    setExploreTopInsights([]);
    setHasExploreStarted(false);
    setExploreChatInput("");
    // Clear persisted session so the next load starts clean
    if (installId) {
      axios.post(`${API_URL}/api/session`, {
        install_id: installId,
        exploreSessionId: null,
        messages: [],
        insights: [],
      }, { withCredentials: true }).catch(() => { });
    }
  };

  const switchToDistill = () => {
    setMode("distill");
    resetOutput();
    // Switching to distill means the explore session is stale — clear it so
    // the chat doesn't bleed back in when the user returns to explore.
    startFreshExplore();
  };

  const sendExploreMessage = async (text?: string) => {
    const msg = (text ?? exploreChatInput).trim();
    if (!msg || isExploreLoading) return;
    setExploreChatInput("");
    setThought("");
    if (!hasExploreStarted) setHasExploreStarted(true);
    const historyBefore = [...exploreMessages];
    const userMsg = { role: "user" as const, content: msg, timestamp: new Date().toISOString() };
    setExploreMessages((prev) => [...prev, userMsg]);
    setIsExploreLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/explore/message`,
        { sessionId: exploreSessionId, message: msg, history: historyBefore },
        { withCredentials: true }
      );
      const data = res.data;
      setExploreSessionId(data.sessionId);
      const assistantMsg = { role: "assistant" as const, content: data.reply, timestamp: new Date().toISOString() };
      const newMessages = [...historyBefore, userMsg, assistantMsg];
      setExploreMessages(newMessages);
      const newInsights = data.insights?.length > 0 ? data.insights : exploreTopInsights;
      if (data.insights?.length > 0) setExploreTopInsights(data.insights);

      // Persist session state (fire-and-forget)
      if (installId) {
        axios.post(`${API_URL}/api/session`, {
          install_id: installId,
          exploreSessionId: data.sessionId,
          messages: newMessages,
          insights: newInsights,
        }, { withCredentials: true }).catch(() => { });
      }
    } catch {
      setExploreMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong — please try again.", timestamp: new Date().toISOString() },
      ]);
    }
    setIsExploreLoading(false);
  };

  const handleExploreTopicChip = (topic: string) => {
    const msg = `I want to explore my thoughts on ${topic}`;
    sendExploreMessage(msg);
  };

  const handlePublishExplore = async (insight: TopInsight, idx: number) => {
    if (!isAuthenticated) { openAuthModal('publish'); return; }
    if (!exploreSessionId || isSummarising) return;
    setSummarisingIdx(idx);
    setIsSummarising(true);
    try {
      const summariseRes = await axios.post(
        `${API_URL}/api/explore/summarise`,
        { sessionId: exploreSessionId, insightText: insight.distilledInsight, history: exploreMessages },
        { withCredentials: true }
      );
      const enriched: DistillResult = summariseRes.data;
      setMode("distill");
      setDistillResult(enriched);
      setGeneratedOutputs({});
      setActiveAngle("thoughtful");
      setActiveFormat("linkedin");
      resetOutput();
      setDistillResult(enriched);
      setShowOutput(true);
      setIsRunning(true);
      startFakeProgress(0, 85, 300);
      const genRes = await axios.post(
        `${API_URL}/api/generate`,
        {
          distilledInsight: enriched.distilledInsight,
          topic: enriched.topic,
          angle: "thoughtful",
          // Explore mode has no single "raw thought" with hashtags — pass any
          // tags from the enriched payload if present (defensive: usually empty).
          userHashtags: extractHashtags((enriched as any).rawThought),
        },
        { withCredentials: true }
      );
      const outputs: Record<FormatKey, string> = genRes.data.outputs;
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setGeneratedOutputs({ thoughtful: outputs });
      setIsRunning(false);
      typeOut(outputs.linkedin || "");
      ctaTimerRef.current = setTimeout(() => setShowDemoCta(true), 2200);
      setTimeout(() => outputSectionRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      const savedId2 = installId ? await persistInsight(installId, enriched, outputs, "thoughtful") : null;
      // Auto-generate carousel for the explore-published insight
      setLastCarouselAngle("thoughtful");
      generateCarousel(enriched, carouselColorTheme, "thoughtful", undefined, savedId2);
    } catch {
      setIsRunning(false);
    }
    setIsSummarising(false);
    setSummarisingIdx(null);
  };

  const handleExploreChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendExploreMessage(); }
  };

  // ── Waitlist ─────────────────────────────────────────────────────────
  const handleWaitlist = (
    email: string,
    setSuccess: (v: boolean) => void,
    setEmailError: (v: boolean) => void,
    setLoading: (v: boolean) => void,
    setServerError: (v: boolean) => void,
  ) => {
    if (!email || !email.includes("@")) { setEmailError(true); return; }
    setLoading(true);
    setServerError(false);
    axios.post(`${API_URL}/api/waitlist`, { email: email.trim().toLowerCase(), source: 'landing' })
      .then(() => { setLoading(false); setSuccess(true); })
      .catch(() => { setLoading(false); setServerError(true); });
  };

  const scrollToWaitlist = () => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });

  const handleLogout = () => {
    axios.get(`${API_URL}/auth/logout`, { withCredentials: true })
      .finally(() => {
        // Clear all local auth state regardless of server response
        localStorage.removeItem('distill_auth_token');
        setIsAuthenticated(false);
        setAuthUserName('');
        setLoggedOutToast(true);
        setTimeout(() => setLoggedOutToast(false), 3000);
      });
  };

  const toggleNotifPanel = () => {
    if (showNotifPanel) {
      setShowNotifPanel(false);
      return;
    }
    // Fetch notifications and mark all read
    const id = installId || localStorage.getItem('distill_install_id') || '';
    axios.get(`${API_URL}/api/notifications?install_id=${encodeURIComponent(id)}&limit=5`, { withCredentials: true })
      .then(r => { if (Array.isArray(r.data)) setNotifs(r.data); })
      .catch(() => { });
    axios.patch(`${API_URL}/api/notifications/read-all`, { install_id: id }, { withCredentials: true })
      .then(() => setNotifCount(0))
      .catch(() => { });
    setShowNotifPanel(true);
  };

  const timeAgo = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // ── Insight history scroll helpers ───────────────────────────────────
  const updateIhsScroll = () => {
    const el = ihsScrollRef.current;
    if (!el) return;
    setIhsCanLeft(el.scrollLeft > 4);
    setIhsCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollIhs = (dir: 1 | -1) => {
    const el = ihsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 476, behavior: 'smooth' }); // ~2 card widths
  };

  // Smart relative timestamp — "now" → "today" → "yesterday" → day name → "Xw ago" → date
  const formatInsightTime = (iso: string): string => {
    const now  = new Date();
    const date = new Date(iso);
    const diffMs   = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 2)  return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const todayStr = now.toDateString();
    if (date.toDateString() === todayStr) return 'today';
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    if (date.toDateString() === yest.toDateString()) return 'yesterday';
    if (diffDays < 7)  return date.toLocaleDateString('en-US', { weekday: 'long' });
    if (diffDays < 14) return '1w ago';
    if (diffDays < 21) return '2w ago';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // ── Render helpers ────────────────────────────────────────────────────
  const renderOutput = () => {
    // ── Edit mode: textarea for all formats ──────────────────────────────
    if (isEditing) {
      return (
        <textarea
          className="out-textarea"
          value={outputText}
          onChange={e => setOutputText(e.target.value)}
          onBlur={handleEditDone}
          autoFocus
        />
      );
    }

    // ── Read-only: newsletter gets special split view ────────────────────
    if (activeFormat === "newsletter") {
      const bodyStart = outputText.indexOf("\n\n");
      const hasBody = bodyStart >= 0;
      const subjectRaw = outputText.match(/^Subject: ([^\n]*)/);
      const subjectVal = subjectRaw ? subjectRaw[1] : outputText.startsWith("Subject") ? outputText.slice(9) : outputText;
      const bodyText = hasBody ? outputText.slice(bodyStart + 2) : "";
      return (
        <div className="email-wrapper">
          <div className="email-header">
            <div className="email-row"><span className="email-field-label">From</span><span className="email-from-val">you@distill.app</span></div>
            <div className="email-row">
              <span className="email-field-label">Subject</span>
              <span className={`email-subject-val${!hasBody && isTyping ? " typing" : ""}`}>{subjectVal}</span>
            </div>
          </div>
          <div className={`email-body${hasBody && isTyping ? " typing" : ""}`}>{bodyText}</div>
        </div>
      );
    }
    return <div className={`out-text${isTyping ? " typing" : ""}`}>{outputText}</div>;
  };

  return (
    <div className="landing">
      {/* Nav */}
      <nav>
        <div className="nav-logo">
          <img src="/favicon.svg" alt="" width="22" height="22" style={{ display:'block', flexShrink:0 }} />
          distill<sup>beta</sup>
        </div>
        <div className="nav-right" ref={notifPanelRef}>
          {/* Notification bell */}
          <button className="notif-bell" onClick={toggleNotifPanel} title="Notifications">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {notifCount > 0 && <span className="notif-badge">{notifCount > 9 ? '9+' : notifCount}</span>}
          </button>
          {showNotifPanel && (
            <div className="notif-panel">
              <div className="notif-panel-header">Notifications</div>
              {notifs.length === 0 ? (
                <div className="notif-empty">No notifications yet</div>
              ) : (
                notifs.map(n => (
                  <div key={n.notification_id} className={`notif-item${n.is_read ? '' : ' notif-item--unread'}`}>
                    <div className="notif-item-title">{n.title}</div>
                    <div className="notif-item-body">{n.body}</div>
                    <div className="notif-item-time">{timeAgo(n.created_at)}</div>
                  </div>
                ))
              )}
            </div>
          )}
          {/* Auth / user */}
          {isAuthenticated ? (
            <div className="nav-user">
              <div className="nav-avatar">
                {authUserName ? authUserName.charAt(0).toUpperCase() : "U"}
              </div>
              <span className="nav-user-name">{authUserName || "Signed in"}</span>
              {isAdminUser && (
                <>
                  <a
                    href="/admin/templates"
                    style={{ color: '#A89FFF', fontSize: '11px', fontWeight: 600, textDecoration: 'none', marginRight: '4px', letterSpacing: '0.03em' }}
                    title="Admin: manage templates"
                  >
                    Admin ↗
                  </a>
                  <a
                    href="/distill-v2"
                    style={{ color: '#7ec8a0', fontSize: '11px', fontWeight: 600, textDecoration: 'none', marginRight: '4px', letterSpacing: '0.03em' }}
                    title="Distill v2 (experimental)"
                  >
                    v2 ↗
                  </a>
                </>
              )}
              <button className="nav-logout-btn" onClick={handleLogout} title="Sign out">
                {/* Power / shutdown icon */}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/>
                  <line x1="12" y1="2" x2="12" y2="12"/>
                </svg>
              </button>
            </div>
          ) : (
            <button className="nav-pill" onClick={() => openAuthModal('nav')}>Sign in</button>
          )}
        </div>
      </nav>

      {/* Logout toast */}
      {loggedOutToast && (
        <div className="logout-toast">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          You've been signed out
        </div>
      )}

      {/* Hero */}
      <section className="hero">
        <div className="app-reframe">
          "The secret of getting ahead is getting started."
          <span className="app-reframe-attr">— Mark Twain</span>
        </div>

        <div className="demo-wrap">
          {/* ── Mode toggle ── */}
          <div className="mode-toggle">
            <button
              className={`mode-btn${mode === "distill" ? " active" : ""}`}
              onClick={switchToDistill}
            >
              ✦ I know my take — Distill it
            </button>
            <button
              className={`mode-btn${mode === "explore" ? " active" : ""}`}
              onClick={switchToExplore}
            >
              ◈ Still finding my take — Explore it
            </button>
          </div>

          {/* ── Distill input ── */}
          {mode === "distill" && (
            <div className="demo-input-row">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div className="demo-input-label">Your rough thought</div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "#c0bdb8" }}>or pick an example →</div>
              </div>
              <div className="demo-input-field">
                <textarea
                  className="thought-input"
                  value={thought}
                  onChange={(e) => setThought(e.target.value)}
                  placeholder="e.g. most founders confuse being busy with making progress…"
                  rows={4}
                />
                <button className="go-btn" data-testid="distill-submit" onClick={() => runDemo()} disabled={isRunning}>
                  {isRunning ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                  {isRunning ? "Distilling…" : "Distill it"}
                </button>
              </div>
              {demoError && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "6px", fontWeight: 500 }}>{demoError}</div>
              )}
              <div className="chips">
                {CHIPS.map((chip, i) => (
                  <div key={i} className={`chip${activeChip === i ? " active" : ""}`} onClick={() => !isRunning && handleChip(i)}>
                    {chip.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Explore — full-width chat (ChatGPT/Claude style) ── */}
          {mode === "explore" && (
            <div className="explore-inline">
              {/* Messages */}
              <div className="explore-msgs" ref={exploreMsgsRef}>
                {/* Bot greeting — always first */}
                <div className="xmsg xmsg--bot">
                  <div className="xavatar">d</div>
                  <div className="xmsg-body">
                    <span className="xmsg-name">distill coach</span>
                    <div className="xmsg-text">
                      I'm here to help you find what you actually think. Start anywhere — a frustration, a belief, something you keep coming back to.
                    </div>
                    {!hasExploreStarted && (
                      <div className="xquick-chips">
                        {EXPLORE_TOPICS.map((t) => (
                          <button key={t} className="xquick-chip" onClick={() => handleExploreTopicChip(t)}>{t}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Conversation */}
                {exploreMessages.map((msg, i) => (
                  <div key={i} className={`xmsg xmsg--${msg.role === "assistant" ? "bot" : "user"}`}>
                    {msg.role === "assistant" && <div className="xavatar">d</div>}
                    <div className="xmsg-body">
                      {msg.role === "assistant" && <span className="xmsg-name">distill coach</span>}
                      <div className="xmsg-text">{msg.content}</div>
                    </div>
                  </div>
                ))}

                {isExploreLoading && (
                  <div className="xmsg xmsg--bot">
                    <div className="xavatar">d</div>
                    <div className="xmsg-body">
                      <span className="xmsg-name">distill coach</span>
                      <div className="xtyping"><span /><span /><span /></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input bar */}
              <div className="explore-input-bar">
                {hasExploreStarted && (
                  <button className="xfresh-btn" onClick={(e) => {
                    const el = e.currentTarget;
                    el.classList.remove('spinning');
                    void el.offsetWidth; // reflow to restart animation
                    el.classList.add('spinning');
                    startFreshExplore();
                  }}>
                    ↺
                  </button>
                )}
                <textarea
                  className="explore-textarea"
                  value={exploreChatInput}
                  onChange={(e) => setExploreChatInput(e.target.value)}
                  onKeyDown={handleExploreChatKeyDown}
                  placeholder={hasExploreStarted ? "Reply…" : "e.g. I've been thinking about why most meetings feel pointless…"}
                  rows={1}
                  disabled={isExploreLoading}
                  data-testid="explore-submit"
                />
                <button
                  className="explore-send-btn"
                  onClick={() => {
                    if (!hasExploreStarted && thought.trim()) { sendExploreMessage(thought); setThought(""); }
                    else sendExploreMessage();
                  }}
                  disabled={(!exploreChatInput.trim() && !thought.trim()) || isExploreLoading}
                >
                  {isExploreLoading ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Insights strip — below chat */}
              <div className={`explore-insights-strip${exploreTopInsights.length > 0 ? " has-insights" : ""}`}>
                <div className="explore-insights-label">
                  ✦ Insights forming
                  {isExploreLoading && <span className="insights-pulse" />}
                </div>
                {exploreTopInsights.length === 0 ? (
                  <div className="explore-insights-hint">Keep sharing — your clearest insights will surface here.</div>
                ) : (
                  <div className="explore-insights-cards">
                    {exploreTopInsights.map((ins, idx) => (
                      <div key={`${ins.distilledInsight.slice(0, 20)}-${idx}`} className="xinsight-card">
                        <div className="xic-score">{ins.score}/10</div>
                        <div className="xic-text">"{ins.distilledInsight}"</div>
                        <div className="xic-meta">
                          <span className="xic-topic">{ins.topic}</span>
                          {ins.toneTags.map((t) => <span key={t} className="xic-tag">{t}</span>)}
                        </div>
                        <button
                          className="xpublish-btn"
                          onClick={() => handlePublishExplore(ins, idx)}
                          disabled={isSummarising}
                        >
                          {isSummarising && summarisingIdx === idx ? (
                            <>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                              </svg>
                              Preparing…
                            </>
                          ) : "Publish this →"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Shared output section (distill + explore both land here) ── */}
          <div className={`demo-output${showOutput ? " open" : ""}`} ref={outputSectionRef}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="output-inner">
              {distillResult && (
                <>
                  <div className="insight-card">
                    <div className="ic-label">✦ Your insight</div>
                    <div className="ic-deco-quote">"</div>
                    <div className="ic-text">{distillResult.distilledInsight}</div>
                    <div className="ic-tags">
                      {distillResult.toneTags.map((t) => <span key={t} className="ic-tag">{t}</span>)}
                    </div>
                  </div>

                  <div className="angle-row">
                    {ANGLES.map((a) => (
                      <button key={a.key} className={`angle-btn${activeAngle === a.key ? " active" : ""}`} onClick={() => handleAngle(a.key)} disabled={isRunning}>
                        {a.label}
                        {generatedOutputs[a.key] && a.key !== activeAngle && <span style={{ marginLeft: "4px", fontSize: "9px", opacity: 0.6 }}>✓</span>}
                      </button>
                    ))}
                  </div>

                  {/* ── Caption output box ── */}
                  <div className="out-box-label">✦ Caption <span className="out-box-label-sub">post this with your carousel on LinkedIn</span></div>
                  <div className="out-box">
                    {renderOutput()}
                    <div className="copy-row">
                      <span className="char-count">{charCount}</span>
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        {/* LinkedIn post icon — copy caption and open LinkedIn */}
                        {!isTyping && outputText && (
                          <>
                            <button className="platform-icon-btn platform-icon-btn--linkedin" title="Post on LinkedIn (caption copied)"
                              onClick={() => { navigator.clipboard.writeText(outputText).catch(() => { }); openShareWindow(`https://www.linkedin.com/post/new`); }}>
                              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                            </button>
                          </>
                        )}
                        <div style={{ width: "1px", height: "16px", background: "var(--border)", margin: "0 2px" }} />
                        {!isTyping && outputText && !isRunning && (
                          isEditing ? (
                            <button className="edit-btn edit-btn--done" onClick={handleEditDone}>✓ Done</button>
                          ) : (
                            <button className="edit-btn" title="Edit" onClick={handleEditStart}>✎</button>
                          )
                        )}
                        <button className={`copy-btn${copied ? " copied" : ""}`} onClick={handleCopy} disabled={!distillResult || isTyping}>
                          {copied ? "✓ Copied!" : (
                            <>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                              Copy
                            </>
                          )}
                        </button>
                        <button
                          className={`share-btn${shareError ? " share-btn--error" : ""}`}
                          data-testid="share-btn"
                          onClick={handleShare}
                          disabled={!distillResult || isSharing || isTyping}
                        >
                          {isSharing ? "…" : shareError ? "✕ Failed" : "Share ↗"}
                        </button>
                        <button className="download-btn" onClick={handleDownloadCard} disabled={!distillResult || isDownloading || isTyping}>
                          {isDownloading ? "…" : "↓"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── Carousel — embedded below the caption ── */}
                  {distillResult && (
                    <ErrorBoundary
                      fallback={
                        <div className="carousel-section" style={{ textAlign: 'center', padding: '32px 0', color: '#666', fontSize: '13px' }}>
                          Carousel couldn't load. <button style={{ background: 'none', border: 'none', color: '#A89FFF', cursor: 'pointer', fontSize: '13px' }} onClick={() => window.location.reload()}>Refresh the page</button> to try again.
                        </div>
                      }
                    >
                    <div className="carousel-section">
                      {/* Section header */}
                      <div className="carousel-section-header">
                        <div className="carousel-section-title">
                          <span>✦ Carousel</span>
                          <span className="carousel-section-badge">PDF</span>
                        </div>
                        <div className="carousel-header-controls">
                          {/* Template picker */}
                          <div className="carousel-tpl-picker-wrap">
                            <button
                              className={`carousel-tpl-pick-btn${selectedTemplateId ? ' locked' : ''}`}
                              onClick={() => setShowTemplatePicker(v => !v)}
                              disabled={isCarouselLoading}
                              title="Choose a specific template"
                            >
                              {selectedTemplateId
                                ? (allCarouselTemplates.find(t => t.id === selectedTemplateId)?.name ?? selectedTemplateId)
                                : '🎲 Auto'}
                              <span className="carousel-tpl-caret">▾</span>
                            </button>
                            {showTemplatePicker && (
                              <>
                                <div className="carousel-tpl-backdrop" onClick={() => setShowTemplatePicker(false)} />
                                <div className="carousel-tpl-dropdown">
                                  <button
                                    className={`carousel-tpl-item${!selectedTemplateId ? ' active' : ''}`}
                                    onClick={() => {
                                      setSelectedTemplateId(null);
                                      setShowTemplatePicker(false);
                                    }}
                                  >
                                    <span>🎲 AI picks for me</span>
                                    <span className="carousel-tpl-item-meta">auto</span>
                                  </button>
                                  {allCarouselTemplates.map(t => (
                                    <button
                                      key={t.id}
                                      className={`carousel-tpl-item${selectedTemplateId === t.id ? ' active' : ''}`}
                                      onClick={() => {
                                        setSelectedTemplateId(t.id);
                                        setShowTemplatePicker(false);
                                        if (distillResult) generateCarousel(distillResult, carouselColorTheme, activeAngle, undefined, undefined, t.id);
                                      }}
                                    >
                                      <span>{t.name}</span>
                                      <span className="carousel-tpl-item-meta">{t.slideCount} slides</span>
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          {/* Try another */}
                          <button
                            className="carousel-regenerate-btn"
                            onClick={() => {
                              if (!distillResult) return;
                              if (selectedTemplateId) {
                                // Re-generate with the locked template
                                generateCarousel(distillResult, carouselColorTheme, activeAngle);
                              } else {
                                // Exclude templates already seen so Gemini picks a fresh one
                                const seenIds = carouselHistory.map(e => e.templateId);
                                const allIds  = allCarouselTemplates.map(t => t.id);
                                const exclude = seenIds.length >= allIds.length ? [] : seenIds;
                                generateCarousel(distillResult, carouselColorTheme, activeAngle, exclude);
                              }
                            }}
                            disabled={isCarouselLoading || !distillResult}
                            title="Try a different carousel template"
                          >
                            {isCarouselLoading ? "…" : "↺ Try another"}
                          </button>
                        </div>
                      </div>

                      {/* History pill bar — shown when ≥2 templates have been generated */}
                      {carouselHistory.length > 1 && (
                        <div className="carousel-history-bar">
                          {carouselHistory.map(entry => {
                            const meta = allCarouselTemplates.find(t => t.id === entry.templateId);
                            return (
                              <button
                                key={entry.templateId}
                                className={`carousel-history-pill${activeHistoryTemplateId === entry.templateId ? ' active' : ''}`}
                                onClick={() => switchCarouselHistory(entry.templateId)}
                                title={meta?.name ?? entry.templateId}
                              >
                                {meta?.name ?? entry.templateId}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Slide preview — single slide with prev/next navigation */}
                      {renderCarouselPreview()}

                      {/* Refine bar */}
                      {carouselSlides && !isCarouselLoading && (
                        <div className="carousel-refine-bar">
                          <input
                            className="carousel-refine-input"
                            placeholder='e.g. "make the hook more aggressive" or "use simpler words"'
                            value={carouselRefineInput}
                            onChange={e => setCarouselRefineInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && handleCarouselRefine()}
                            disabled={isCarouselRefining}
                          />
                          <button
                            className="carousel-refine-submit"
                            disabled={isCarouselRefining || !carouselRefineInput.trim()}
                            onClick={handleCarouselRefine}
                          >
                            {isCarouselRefining ? "Refining…" : "Refine →"}
                          </button>
                        </div>
                      )}

                      {/* Download button(s) */}
                      <div className="carousel-download-row">
                        <button
                          className="carousel-download-btn"
                          onClick={handleCarouselDownload}
                          disabled={!carouselSlides || isCarouselLoading || isCarouselDownloading}
                        >
                          {isCarouselDownloading ? "Preparing…" : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                              Download PDF
                            </>
                          )}
                        </button>
                        {carouselRenderer === 'pptx' && carouselPptxBase64 && (
                          <button
                            className="carousel-download-btn carousel-download-pptx"
                            onClick={handleDownloadPptx}
                            disabled={!carouselSlides || isCarouselLoading}
                            title="Download editable PowerPoint file — useful if the PDF preview has layout glitches"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Download PPT
                          </button>
                        )}
                        {isAuthenticated && !isPro && !isAdminUser && (
                          <span className="carousel-pro-hint">
                            {downloadsThisMonth >= FREE_DOWNLOAD_LIMIT
                              ? <>Limit reached · <button className="carousel-pro-link" onClick={() => setShowUpgradeModal(true)}>Upgrade for unlimited →</button></>
                              : <>{FREE_DOWNLOAD_LIMIT - downloadsThisMonth} free download{FREE_DOWNLOAD_LIMIT - downloadsThisMonth !== 1 ? 's' : ''} left this month</>
                            }
                          </span>
                        )}
                        {!isAuthenticated && (() => {
                          const spotsLeft = (activityData?.earlyBirdLimit ?? 100) - (activityData?.earlyBirdUsed ?? 0);
                          const nthAdopter = (activityData?.registeredUsers ?? 0) + 1;
                          const hasSpots = spotsLeft > 0;
                          return (
                            <button
                              className="carousel-early-adopter-nudge"
                              onClick={() => openAuthModal('download')}
                            >
                              {hasSpots ? (
                                <>
                                  🎉 <strong>Sign up free</strong> → 1 month unlimited ·{' '}
                                  be the <strong>#{nthAdopter} early adopter</strong>
                                  <span className="carousel-nudge-spots">{spotsLeft} spots left</span>
                                </>
                              ) : (
                                <>Sign in to download your carousel</>
                              )}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    </ErrorBoundary>
                  )}

                  {!isRunning && outputText && !isTyping && !isEditing && (
                    <div className="revise-bar">
                      <div className="revise-chips">
                        {["Shorter", "Punchier hook", "More personal", "Add a story", "More specific", "Stronger ending"].map(chip => (
                          <button
                            key={chip}
                            className="revise-chip"
                            disabled={isRevising}
                            onClick={() => handleRevise(chip)}
                          >{chip}</button>
                        ))}
                      </div>
                      <div className="revise-row">
                        <input
                          className="revise-input"
                          placeholder="e.g. make it sound less corporate…"
                          value={reviseInput}
                          onChange={e => setReviseInput(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleRevise(reviseInput)}
                          disabled={isRevising}
                        />
                        <button
                          className="revise-btn"
                          disabled={isRevising || !reviseInput.trim()}
                          onClick={() => handleRevise(reviseInput)}
                        >{isRevising ? "Revising…" : "Revise →"}</button>
                      </div>
                    </div>
                  )}

                  <div className="pov-strip">
                    <div className="pov-card agree">
                      <div className="pov-label">Similar take</div>
                      <div className="pov-text">{distillResult.pov.agree}</div>
                    </div>
                    <div className="pov-card disagree">
                      <div className="pov-label">Opposing view</div>
                      <div className="pov-text">{distillResult.pov.disagree}</div>
                    </div>
                  </div>

                  {showDemoCta && (
                    <div className="demo-cta-strip">
                      <p>This is just the demo. The real thing learns your voice over time.</p>
                      <button className="nav-pill" style={{ fontSize: "13px", padding: "9px 24px" }} onClick={() => openAuthModal('nav')}>
                        Sign in to save your insights →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Recent Insights history ── */}
        {insightHistory.length > 0 && (
          <div className="insight-history-strip">
            <div className="ihs-header">
              <div className="ihs-label">✦ Your recent insights</div>
              {/* Prev/Next keyboard-style navigation through history */}
              {insightHistory.length > 1 && (
                <div className="ihs-nav-arrows">
                  <button
                    className="ihs-nav-btn"
                    onClick={() => {
                      historyDispatch({ type: 'NAVIGATE', dir: -1 });
                      const nextIdx = activeHistoryIdx !== null ? Math.max(0, activeHistoryIdx - 1) : 0;
                      restoreInsight(insightHistory[nextIdx], nextIdx);
                    }}
                    disabled={activeHistoryIdx === 0}
                    aria-label="Previous insight"
                  >‹</button>
                  <span className="ihs-nav-count">
                    {activeHistoryIdx !== null ? `${activeHistoryIdx + 1} / ${insightHistory.length}` : insightHistory.length}
                  </span>
                  <button
                    className="ihs-nav-btn"
                    onClick={() => {
                      historyDispatch({ type: 'NAVIGATE', dir: 1 });
                      const nextIdx = activeHistoryIdx !== null
                        ? Math.min(activeHistoryIdx + 1, insightHistory.length - 1)
                        : 0;
                      restoreInsight(insightHistory[nextIdx], nextIdx);
                    }}
                    disabled={activeHistoryIdx === insightHistory.length - 1}
                    aria-label="Next insight"
                  >›</button>
                </div>
              )}
            </div>
            <div className="ihs-scroll-wrapper">
              {ihsCanLeft && (
                <button className="ihs-arrow ihs-arrow-left" onClick={() => scrollIhs(-1)} aria-label="Scroll left">‹</button>
              )}
              <div
                className="ihs-cards"
                ref={ihsScrollRef}
                onScroll={updateIhsScroll}
                onLoad={updateIhsScroll}
              >
                {insightHistory.map((ins, idx) => (
                  <div
                    key={ins.id}
                    className={`ihs-card${activeHistoryIdx === idx ? ' ihs-card-active' : ''}`}
                    onClick={() => restoreInsight(ins, idx)}
                  >
                    <div className="ihs-card-top">
                      <div className="ihs-topic">{ins.topic}</div>
                      <div className="ihs-time">{formatInsightTime(ins.createdAt)}</div>
                    </div>
                    <div className="ihs-text">"{ins.distilledInsight}"</div>
                    <div className="ihs-tags">
                      {ins.toneTags.slice(0, 2).map(t => <span key={t} className="ihs-tag">{t}</span>)}
                      {ins.carouselSlides && (
                        <span className="ihs-tag ihs-tag-carousel" title="Carousel ready to restore">🎠</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {ihsCanRight && (
                <button className="ihs-arrow ihs-arrow-right" onClick={() => scrollIhs(1)} aria-label="Scroll right">›</button>
              )}
            </div>
          </div>
        )}

      </section>

      {/* Share modal */}
      {showShareModal && shareUrl && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <span className="share-modal-title">Share your insight</span>
              <button className="share-modal-close" onClick={() => setShowShareModal(false)}>✕</button>
            </div>
            <div className="share-url-row">
              <span className="share-url-text">{shareUrl}</span>
              <button className={`share-copy-btn${shareCopied ? " copied" : ""}`} onClick={handleCopyShareUrl}>
                {shareCopied ? "✓" : "Copy"}
              </button>
            </div>
            <div className="share-social-label">Share on</div>
            <div className="share-social-row">
              <button className="share-social-btn" title="WhatsApp"
                onClick={() => openShareWindow(`https://wa.me/?text=${encodeURIComponent((distillResult?.hook || shareUrl) + ' ' + shareUrl)}`)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              </button>
              <button className="share-social-btn" title="X / Twitter"
                onClick={() => openShareWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(distillResult?.hook || distillResult?.distilledInsight || '')}&url=${encodeURIComponent(shareUrl)}`)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </button>
              <button className="share-social-btn" title="LinkedIn"
                onClick={() => openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </button>
              <button className="share-social-btn" title="Telegram"
                onClick={() => openShareWindow(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(distillResult?.hook || '')}`)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pro upgrade modal — QR payment flow */}
      {showUpgradeModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowUpgradeModal(false); setPaymentSubmitted(false); setPaymentTxnId(''); }}>
          <div className="auth-modal upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <button className="auth-modal-close" onClick={() => { setShowUpgradeModal(false); setPaymentSubmitted(false); setPaymentTxnId(''); }}>✕</button>
            {!paymentSubmitted ? (
              <>
                <div className="auth-modal-icon">✦</div>
                <p className="auth-modal-title">Download this carousel</p>
                <p className="auth-modal-sub">
                  <strong>₹1 / carousel</strong> during launch · ₹11 after 48 hours.<br />
                  First 100 users: <strong>free</strong> — check your spot above.
                </p>
                <div className="upgrade-qr-wrap">
                  {/* Replace /qr-payment.png with your actual UPI QR code image */}
                  <img src="/qr-payment.png" alt="Scan to pay via UPI" className="upgrade-qr-img" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  <div className="upgrade-qr-note">Scan with any UPI app · Pay ₹1</div>
                </div>
                <input
                  className="upgrade-txn-input"
                  placeholder="Paste UPI transaction ID after payment"
                  value={paymentTxnId}
                  onChange={e => setPaymentTxnId(e.target.value)}
                />
                <button
                  className="auth-google-btn"
                  style={{ background: paymentTxnId.trim().length > 4 ? 'var(--accent)' : '#ccc', color: '#fff', border: 'none', cursor: paymentTxnId.trim().length > 4 ? 'pointer' : 'default' }}
                  disabled={paymentTxnId.trim().length <= 4}
                  onClick={() => {
                    if (paymentTxnId.trim().length > 4) {
                      // Fire-and-forget — store txn id on backend
                      axios.post(`${API_URL}/api/payments/upi`, { txnId: paymentTxnId.trim(), installId }).catch(() => {});
                      setPaymentSubmitted(true);
                    }
                  }}
                >
                  Submit transaction ID →
                </button>
              </>
            ) : (
              <>
                <div className="auth-modal-icon">🎉</div>
                <p className="auth-modal-title">Payment received!</p>
                <p className="auth-modal-sub">We'll verify and activate your download within the hour. You'll get a notification here.</p>
                <button className="auth-google-btn" style={{ background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={() => { setShowUpgradeModal(false); setPaymentSubmitted(false); setPaymentTxnId(''); }}>
                  Got it ✓
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auth modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="auth-modal-close" onClick={() => setShowAuthModal(false)}>✕</button>
            <div className="auth-modal-icon">✦</div>
            <p className="auth-modal-title">{AUTH_MODAL_COPY[authModalReason].title}</p>
            <p className="auth-modal-sub">{AUTH_MODAL_COPY[authModalReason].sub}</p>
            <a
              className="auth-google-btn"
              href={`${API_URL}/auth/google?install_id=${encodeURIComponent(installId)}`}
            >
              <svg className="auth-google-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </a>
          </div>
        </div>
      )}

      {/* Off-screen insight card for PNG download */}
      {distillResult && (
        <InsightCard
          ref={cardRef}
          distilledInsight={distillResult.distilledInsight}
          topic={distillResult.topic}
          toneTags={distillResult.toneTags}
          hook={distillResult.hook}
        />
      )}
    </div>
  );
};

export default DistillPage;
